// Meat-doneness technique validator. Detects recipes that contain
// a protein canonical and lack an explicit internal-temperature
// cue on the cook step. USDA safe-floor temps are mechanical;
// every chicken/pork/ground recipe should say "internal temp X°F"
// somewhere so the cook knows when to pull it. This validator
// catches the omission and auto-fixes it by appending the
// appropriate temp to the cook step's doneCue.
//
// Companion to the baking-science validator. Same shape:
// validate() returns { ok, issues, warnings }; autoFix() returns
// { recipe, changes }. Plugged into the recipe-level orchestrator
// at src/lib/recipeValidator.js.

// USDA / industry-standard safe internal temps. Float (no .0) so
// the formatter doesn't print "165.0°F".
const TEMP_F = {
  // Poultry — all forms cook to 165°F including ground.
  chicken:        165,
  chicken_breast: 165,
  chicken_thigh:  165,
  chicken_wing:   165,
  chicken_leg:    165,
  chicken_tenderloin: 165,
  ground_chicken: 165,
  turkey:         165,
  turkey_breast:  165,
  ground_turkey:  165,
  duck:           165,
  duck_breast:    155, // medium — duck is the one exception
  // Pork — 145°F whole-cuts (with 3 min rest); 160°F ground.
  pork_chop:      145,
  pork_shoulder:  195, // pulled pork target
  pork_belly:     195, // braised
  pork_loin:      145,
  pork_tenderloin: 145,
  bacon:          null, // crisp-by-eye, no internal temp
  ground_pork:    160,
  ham:            145,
  // Beef — varies by doneness target; default medium-rare.
  ribeye:         130,
  steak:          130,
  beef_chuck:     195, // braise / pot roast target
  brisket:        205, // long-cook collagen breakdown
  short_ribs:     205,
  ground_beef:    160, // ground always 160°F
  // Lamb
  lamb_chop:      130,
  ground_lamb:    160,
  lamb_shoulder:  195,
  // Seafood — 145°F floor; salmon often pulled at 125-130°F for
  // restaurant doneness, but 145°F is the USDA safety floor.
  salmon:         125, // medium / restaurant-style
  tuna:           125, // seared rare
  cod:            145,
  halibut:        145,
  shrimp:         145,
  scallops:       145,
};

// Which canonicals require a temp cue. Bacon is excluded because
// it's eyeball-driven (crisp browning, not internal temp).
const MEAT_CANONICALS = new Set(
  Object.keys(TEMP_F).filter((k) => TEMP_F[k] != null)
);

// Safety FLOORS for range validation (°F). TEMP_F is the pull
// TARGET; this is the number below which a written internal temp is
// dangerous, not just off-style. They differ most on braise cuts:
// brisket's 205°F is a collagen/texture target, but the meat is
// SAFE at 145°F — so a recipe saying "pull the brisket at 185°F"
// is fine, while "cook the chicken to 140°F" is a real hazard.
// Floors sit slightly under the USDA number to allow carryover-rest
// phrasing ("pull at 160°F, rest to 165°F").
const MIN_SAFE_F = {
  chicken: 160, chicken_breast: 160, chicken_thigh: 160,
  chicken_wing: 160, chicken_leg: 160, chicken_tenderloin: 160,
  ground_chicken: 160, turkey: 160, turkey_breast: 160,
  ground_turkey: 160, duck: 160,
  duck_breast: 130,             // medium-rare duck breast is standard practice
  pork_chop: 140, pork_loin: 140, pork_tenderloin: 140, ham: 140,
  pork_shoulder: 140, pork_belly: 140,   // safe well below the 195°F texture target
  ground_pork: 155,
  ribeye: 115, steak: 115, lamb_chop: 115,   // rare is a choice, not a hazard
  beef_chuck: 140, brisket: 140, short_ribs: 140, lamb_shoulder: 140,
  ground_beef: 155, ground_lamb: 155,
  salmon: 110, tuna: 110,       // seared-rare tuna is intentional
  cod: 125, halibut: 125, shrimp: 125, scallops: 125,
};

// Grounding accessors for the generation payload (B4). The client
// stamps these onto lockedIngredients so the final prompt's
// GROUNDED NUMBERS block writes real temps, not stylistic ones.
export function targetInternalTempF(canonicalId) {
  return TEMP_F[canonicalId] ?? null;
}
export function minSafeInternalTempF(canonicalId) {
  return MIN_SAFE_F[canonicalId] ?? null;
}

// Friendly cue text by canonical for the doneCue auto-fix output.
// Falls back to a generic "Internal temp: X°F" if no override.
const CUE_OVERRIDES = {
  chicken_breast: "Internal temp 165°F at the thickest part; juices run clear.",
  chicken_thigh:  "Internal temp 165°F; meat pulls easily from the bone.",
  ground_chicken: "Internal temp 165°F; no pink remains.",
  pork_chop:      "Internal temp 145°F, then rest 3 min off heat.",
  pork_shoulder:  "Internal temp 195°F; meat shreds easily with a fork.",
  brisket:        "Internal temp 205°F; probe slides in like room-temp butter.",
  short_ribs:     "Internal temp 205°F; meat pulls from the bone.",
  ribeye:         "Internal temp 130°F for medium-rare (135°F medium).",
  steak:          "Internal temp 130°F for medium-rare (135°F medium).",
  ground_beef:    "Internal temp 160°F; no pink remains.",
  ground_pork:    "Internal temp 160°F.",
  salmon:         "Internal temp 125°F at the thickest part for moist medium; 145°F for fully cooked.",
  shrimp:         "Pink and opaque; internal temp 145°F. Curled into a tight C-shape (not a tight O).",
  scallops:       "Internal temp 145°F; opaque center with a deep crust.",
};

// Regex matching ANY internal-temperature reference in step prose
// or doneCue. Accepts "165°F" / "165 F" / "165 degrees" / "internal
// temp 165" / "until 165°F" / "thermometer reads 165". Word-boundary
// on the digits so "1650g" doesn't false-match.
const TEMP_RE = /\b(1[0-9][0-9]|2[0-2][0-9])\s*(°|degrees?\s*)?\s*f\b|\binternal\s+temp(?:erature)?\s*(?:of|reaches|reads|hits)?\s*\d{2,3}|thermometer\s+reads\s+\d{2,3}/i;

// Cooking-verb regex for matching "cook step" candidates.
const COOK_VERB_RE = /\b(cook|sear|roast|grill|fry|saut[eé]|bake|broil|braise|simmer|poach|steam|smoke|brown|render)\b/i;

// Pull every plausible internal-temp number (100-229°F) out of step
// text so presence can be range-checked, not just detected. Ambient
// temps (350°F oven, 450°F pan) fall outside the window; a 225°F
// smoker CAN land inside it, which is why the range check only
// flags when EVERY extracted temp is below the safety floor — one
// in-range temp anywhere in the step is taken as the internal cue.
function extractTempsF(text) {
  const out = [];
  const re = /\b(\d{2,3})\s*(?:°\s*f\b|degrees?\b|f\b)|(?:internal\s+temp(?:erature)?(?:\s*(?:of|reaches|reads|hits))?|thermometer\s+reads)\s*(\d{2,3})/gi;
  let m;
  while ((m = re.exec(String(text || ""))) !== null) {
    const n = Number(m[1] || m[2]);
    if (Number.isFinite(n) && n >= 100 && n <= 229) out.push(n);
  }
  return out;
}

function stepText(s) {
  return s?.instruction || s?.body || s?.text || "";
}

function findMeatCanonicalsInRecipe(recipe) {
  const ings = recipe?.ingredients || [];
  const out = [];
  const seen = new Set();
  for (const ing of ings) {
    const id = ing?.ingredientId;
    if (!id || seen.has(id)) continue;
    if (MEAT_CANONICALS.has(id)) {
      out.push(id);
      seen.add(id);
    }
  }
  return out;
}

// Find the step that "cooks" the given meat canonical. Heuristic:
// step instruction mentions the canonical's name OR its bare-form
// noun (chicken_breast → "chicken" / "breast"), AND contains a
// cooking verb. Falls back to the recipe's longest-timer step if
// no specific match.
function findCookStepForMeat(recipe, canonicalId) {
  const steps = recipe?.steps || [];
  // Build a few candidate name tokens for the meat. "chicken_breast"
  // → ["chicken breast", "chicken", "breast"]. Lowercase + word-
  // boundary matching against step text.
  const bare = canonicalId.replace(/_/g, " ");
  const parts = bare.split(/\s+/).filter((p) => p.length >= 3);
  const tokens = [bare, ...parts];
  for (let i = 0; i < steps.length; i += 1) {
    const txt = stepText(steps[i]).toLowerCase();
    if (!COOK_VERB_RE.test(txt)) continue;
    const matchesMeat = tokens.some((t) => txt.includes(t));
    if (matchesMeat) return i;
  }
  // Fallback — longest-timer step that has a cook verb.
  let bestIdx = -1;
  let bestTimer = 0;
  for (let i = 0; i < steps.length; i += 1) {
    const txt = stepText(steps[i]);
    if (!COOK_VERB_RE.test(txt)) continue;
    const sec = Math.max(
      Number(steps[i]?.timer) || 0,
      ...(Array.isArray(steps[i]?.timers) ? steps[i].timers.map((t) => Number(t?.seconds) || 0) : []),
    );
    if (sec > bestTimer) {
      bestTimer = sec;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function isMeatRecipe(recipe) {
  return findMeatCanonicalsInRecipe(recipe).length > 0;
}

export function validateMeatDoneness(recipe) {
  const meats = findMeatCanonicalsInRecipe(recipe);
  if (meats.length === 0) {
    return { ok: true, issues: [], warnings: [] };
  }
  const issues = [];
  const warnings = [];
  for (const id of meats) {
    const cookIdx = findCookStepForMeat(recipe, id);
    if (cookIdx === -1) {
      // Meat is in the ingredients but no step cooks it — that's a
      // bigger structural problem; let it surface as a warning.
      warnings.push({
        kind: "meat_no_cook_step",
        severity: "warning",
        canonicalId: id,
        message: `${id.replace(/_/g, " ")} is in the ingredient list but no step cooks it.`,
        suggestion: "Add a step that cooks the protein, including its internal temp cue.",
      });
      continue;
    }
    const step = recipe.steps[cookIdx];
    const stepBody = stepText(step);
    const cue = step?.doneCue || "";
    const text = `${stepBody} ${cue}`;
    if (TEMP_RE.test(text)) {
      // A temp is present — range-check it instead of rubber-stamping.
      // Previously ANY 100-229°F passed, so "cook the chicken to
      // 140°F" shipped as "validated." Flag only when every temp in
      // the step sits below the protein's safety floor (one in-range
      // number anywhere is assumed to be the internal cue; ambient
      // smoker/oven temps above the floor don't false-positive).
      const floor = MIN_SAFE_F[id];
      const temps = extractTempsF(text);
      if (floor != null && temps.length > 0 && temps.every((t) => t < floor)) {
        issues.push({
          kind: "meat_temp_unsafe",
          severity: "blocker",
          canonicalId: id,
          stepId: step.id,
          stepIndex: cookIdx,
          message: `Step "${step.title || step.id}" cooks ${id.replace(/_/g, " ")} to ${Math.max(...temps)}°F — below the ${floor}°F safety floor.`,
          suggestion: CUE_OVERRIDES[id] || `Pull at internal temp ${TEMP_F[id]}°F.`,
        });
      }
      continue;
    }
    // Missing temp on the cook step that owns this meat.
    issues.push({
      kind: "meat_temp_missing",
      severity: "blocker",
      canonicalId: id,
      stepId: step.id,
      stepIndex: cookIdx,
      message: `Step "${step.title || step.id}" cooks ${id.replace(/_/g, " ")} but doesn't mention internal temp.`,
      suggestion: CUE_OVERRIDES[id] || `Pull at internal temp ${TEMP_F[id]}°F.`,
    });
  }
  return { ok: issues.length === 0, issues, warnings };
}

export function autoFixMeatDoneness(recipe, findings) {
  const issues = Array.isArray(findings?.issues) ? findings.issues : [];
  if (issues.length === 0) return { recipe, changes: [] };
  const steps = [...(recipe.steps || [])];
  const changes = [];
  for (const f of issues) {
    if (f.kind !== "meat_temp_missing" && f.kind !== "meat_temp_unsafe") continue;
    if (f.stepIndex < 0 || f.stepIndex >= steps.length) continue;
    const step = { ...steps[f.stepIndex] };
    const baseCue = CUE_OVERRIDES[f.canonicalId] || `Internal temp ${TEMP_F[f.canonicalId]}°F.`;
    // Unsafe temps get an explicit correction prefix — the wrong
    // number stays visible in the prose, so the cue must clearly
    // supersede it rather than read as a second opinion.
    const cue = f.kind === "meat_temp_unsafe" ? `Safety correction: ${baseCue}` : baseCue;
    // Prefer appending to doneCue (the structured "ready when..."
    // field); falls back to instruction body when doneCue isn't on
    // the row.
    if ("doneCue" in step && (typeof step.doneCue === "string" || step.doneCue == null)) {
      const prior = (step.doneCue || "").trim();
      step.doneCue = prior ? `${prior} ${cue}` : cue;
    } else {
      const orig = stepText(step).trim();
      const append = ` ${cue}`;
      if ("instruction" in step) step.instruction = orig + append;
      else if ("body" in step)   step.body        = orig + append;
      else                       step.instruction = orig + append;
    }
    steps[f.stepIndex] = step;
    changes.push({
      kind: f.kind,
      summary: `Added "${cue}" to the ${f.canonicalId.replace(/_/g, " ")} cook step.`,
    });
  }
  return { recipe: { ...recipe, steps }, changes };
}
